import { DateTime } from "luxon";
import { v4 as uuidv4 } from "uuid";
import { medicalDefaultTime } from "../../../../components/RecurringShift/RecurringShiftMasterSchedule/utils";
import { configuredAmount, configuredAmountRequired } from "../../../../utils/censusResolution";
import {
	type DepartmentLocation,
	type DepartmentType,
	EditDepartmentLocationConfigMode,
	type NurseFullRole,
	type NurseRole,
	type RatioLadder,
	type ShiftType,
} from "../../../../utils/models";
import { getShiftIndexByShiftTimes } from "../../../../utils/utilFunctions";
import { DEFAULT_DEPARTMENT_LOCATION, WEEKDAYS } from "../../../Settings/DepartmentConfiguration/utils";
import type { EmrMapping } from "./locationEmrMapping";
import { seedRatioLadderFromTargets, toLocationRoles } from "./ratioLadderCoverage";

/**
 * The location popup's form data and its conversions to and from `DepartmentLocation`. Pure functions,
 * moved verbatim out of `useEditDepartmentLocationConfigState` (APP-780 PR1).
 */

export type DayAmountRow = {
	id: string;
	/** short day names: ["sun", "mon", ...] */
	days: string[];
	amount: number;
	amountRequired: number;
};

export type LocationConfigSlimData = {
	name: string;
	section: string;
	roles: (NurseFullRole | "all")[];
	/** Job ID for this location (used when job_management_enabled FF is on) */
	jobId: string;
	/** HH:MM format */
	shiftStartTime: string;
	/** HH:MM format */
	shiftEndTime: string;
	/** short lowercase names of the days of the week — derived from dayAmountRows */
	days: string[];
	/** Department type for Synergy MS mode */
	departmentType?: DepartmentType;
	/** Unit associated with this location */
	unit?: string;
	/** Floor associated with this location */
	floor?: string;
	/** Ratio ladder configuration for this location */
	ratioLadder: RatioLadder[];
	/** Per-day staff amounts, keyed by day abbreviation — derived from dayAmountRows */
	dayAmounts: Record<string, number>;
	/** Per-day required amounts, keyed by day abbreviation — derived from dayAmountRows */
	dayAmountsRequired: Record<string, number>;
	/** Grouped rows: each row has days + amount + amountRequired */
	dayAmountRows: DayAmountRow[];
	/** PCC rooms selected for this location */
	rooms: string[];
	/** Admin flag: staff here are tied to the location (e.g. a 1:1 sitter) and excluded from the moveable ratio pool */
	cantMove: boolean;
};

/** The per-day fields `syncRowsToFlatMaps` derives from the staffing target rows. */
export type DayAmountFlatMaps = Pick<LocationConfigSlimData, "dayAmounts" | "dayAmountsRequired" | "days">;

/** Derives flat dayAmounts, dayAmountsRequired, and days from dayAmountRows */
export const syncRowsToFlatMaps = (rows: DayAmountRow[]): DayAmountFlatMaps => {
	const dayAmounts: Record<string, number> = {};
	const dayAmountsRequired: Record<string, number> = {};
	const allDays: string[] = [];
	for (const row of rows) {
		for (const day of row.days) {
			dayAmounts[day] = row.amount;
			dayAmountsRequired[day] = row.amountRequired;
			if (!allDays.includes(day)) allDays.push(day);
		}
	}
	const days = WEEKDAYS.filter((d) => allDays.includes(d));
	return { dayAmounts, dayAmountsRequired, days };
};

export const getFormConfigData = (
	isMedicalDepartment: boolean,
	mode: EditDepartmentLocationConfigMode,
	existingConfig?: DepartmentLocation,
	initialSection?: string,
): LocationConfigSlimData => {
	if (existingConfig) {
		// Read per-day amounts directly from the location's dayAmounts JSONB
		const dayAmounts: Record<string, number> = {};
		const dayAmountsRequired: Record<string, number> = {};
		// DEFAULT: every weekday — `days` is typed as always present, but a payload that omits it (the
		// backend's legacy backfill reads a missing `days` the same way) is a location that runs every day.
		const days = existingConfig.days ?? [...WEEKDAYS];

		for (const day of days) {
			// A running day missing from the dayAmounts JSONB (rows written before per-day amounts) opens with the
			// amounts every other surface reads for it — the row's `amount` / `amountRequired`, the busiest configured
			// day's — as the collapsed row, the needs strip, the Schedule Template cells and the backend's staffing
			// needs do (`configuredAmount`), so opening and saving such a row keeps what it is planned for.
			dayAmounts[day] = configuredAmount(existingConfig, day);
			dayAmountsRequired[day] = configuredAmountRequired(existingConfig, day);
		}

		const dayAmountRows = groupDaysByAmount(days, dayAmounts, dayAmountsRequired).map((group) => ({
			id: uuidv4(),
			days: group.days,
			amount: group.amount,
			amountRequired: group.amountRequired,
		}));
		return {
			// DEFAULT: "" / [] — name, section and roles are typed as always present and the job is optional; a
			// payload missing one opens with that field empty, and the Save checks then ask for what's required.
			name: existingConfig.location ?? "",
			section: existingConfig.section ?? "",
			roles: existingConfig.roles ?? [],
			jobId: existingConfig.jobId ?? "",
			// DEFAULT: "06:00" / "18:00" are unreachable — `substring` always returns a string. They mirror
			// the Create-mode hours below and are kept because this block was moved verbatim.
			shiftStartTime: isMedicalDepartment ? medicalDefaultTime : (existingConfig.shift_start.substring(0, 5) ?? "06:00"),
			shiftEndTime: isMedicalDepartment ? medicalDefaultTime : (existingConfig.shift_end.substring(0, 5) ?? "18:00"),
			days,
			departmentType: existingConfig.departmentType,
			unit: existingConfig.unit,
			floor: existingConfig.floor,
			// DEFAULT: [] — the ladder comes from a separate join and is absent on payloads that skip it (and
			// on non-ratio departments); a ratio department then seeds one in `getInitialConfigData`.
			ratioLadder: existingConfig.ratioLadder ?? [],
			dayAmounts,
			dayAmountsRequired,
			dayAmountRows,
			// DEFAULT: [] — rooms come from a separate join and are absent on payloads that skip it — no rooms mapped.
			rooms: (existingConfig.rooms ?? []).map((r) => r.room_name),
			// DEFAULT: false — covers locations cached/loaded before the field existed; the server backfills it.
			cantMove: existingConfig.cantMove ?? false,
		};
	}
	if (mode === EditDepartmentLocationConfigMode.Create) {
		const dayAmounts: Record<string, number> = {};
		const dayAmountsRequired: Record<string, number> = {};
		for (const day of WEEKDAYS) {
			dayAmounts[day] = 1;
			dayAmountsRequired[day] = 1;
		}
		return {
			name: "",
			// DEFAULT: "" — no section band was clicked, so the new location starts without a section.
			section: initialSection ?? "",
			roles: [],
			jobId: "",
			shiftStartTime: "06:00",
			shiftEndTime: "18:00",
			days: [...WEEKDAYS],
			departmentType: undefined,
			unit: undefined,
			floor: undefined,
			ratioLadder: [],
			dayAmounts,
			dayAmountsRequired,
			dayAmountRows: [{ id: uuidv4(), days: [...WEEKDAYS], amount: 1, amountRequired: 1 }],
			rooms: [],
			cantMove: false,
		};
	}
	throw new Error("Invalid mode or missing existing config");
};

/**
 * A ratio location is staffed only by its ladder, so one without a ladder opens with a 0–∞ ladder
 * seeded from its staffing targets (a new location's "every day, 1" target gives 0–∞ × 1). Nothing is
 * saved until the admin clicks Save.
 */
export const getInitialConfigData = (
	isRatioDepartment: boolean,
	departmentRoles: NurseRole[],
	...formArgs: Parameters<typeof getFormConfigData>
): LocationConfigSlimData => {
	const data = getFormConfigData(...formArgs);
	if (!isRatioDepartment || data.ratioLadder.length > 0) return data;
	const owner = { roles: toLocationRoles(data.roles, departmentRoles), jobId: data.jobId };
	return { ...data, ratioLadder: seedRatioLadderFromTargets(data.dayAmountRows, owner) };
};

/** The saved row's per-day staffing fields: what `slimDataToDepartmentLocation` and an inline row edit both save. */
export type DayAmountFields = Pick<DepartmentLocation, "dayAmounts" | "amount" | "amountRequired" | "days">;

/**
 * The per-day flat maps as a saved row stores them: a `dayAmounts` entry per running day, and `amount` /
 * `amountRequired` as the busiest day's. Pure; moved verbatim out of `slimDataToDepartmentLocation` (APP-780 PR5).
 */
export const dayAmountFieldsOf = (flatMaps: DayAmountFlatMaps): DayAmountFields => {
	const dayAmounts: Record<string, { amount: number; amount_required: number }> = {};
	for (const day of flatMaps.days) {
		dayAmounts[day] = {
			// DEFAULT: 1 — unreachable from the form and the inline row edit, whose `days` are derived from the same
			// rows (or rungs) as the amounts (`syncRowsToFlatMaps`, `computeDayAmountsFromRatioLadder`); a day without
			// an amount saves the new-location staffing of 1.
			amount: flatMaps.dayAmounts[day] ?? 1,
			amount_required: flatMaps.dayAmountsRequired[day] ?? 1,
		};
	}
	return {
		dayAmounts,
		amount: Math.max(...Object.values(flatMaps.dayAmounts), 0),
		amountRequired: Math.max(...Object.values(flatMaps.dayAmountsRequired), 0),
		days: flatMaps.days,
	};
};

/** The department's shift type for "HH:mm" hours. Pure; moved verbatim out of `slimDataToDepartmentLocation` (APP-780 PR5). */
export const shiftTypeOf = (shiftStartTime: string, shiftEndTime: string, departmentShiftTypes: ShiftType[]): ShiftType =>
	departmentShiftTypes[
		getShiftIndexByShiftTimes({
			startTime: DateTime.fromFormat(shiftStartTime, "HH:mm"),
			endTime: DateTime.fromFormat(shiftEndTime, "HH:mm"),
		})
	];

export const slimDataToDepartmentLocation = (
	data: LocationConfigSlimData,
	departmentShiftTypes: ShiftType[],
	locationId?: string,
): Partial<DepartmentLocation> => {
	const dayFields = dayAmountFieldsOf(data);
	return {
		location: data.name.trim(),
		section: data.section.trim(),
		dayAmounts: dayFields.dayAmounts,
		amount: dayFields.amount,
		amountRequired: dayFields.amountRequired,
		roles: data.roles,
		jobId: data.jobId,
		shift_start: data.shiftStartTime,
		shift_end: data.shiftEndTime,
		days: dayFields.days,
		shift_type: shiftTypeOf(data.shiftStartTime, data.shiftEndTime, departmentShiftTypes),
		departmentType: data.departmentType,
		unit: data.unit,
		floor: data.floor,
		ratioLadder: data.ratioLadder.map((rl) => ({
			// DEFAULT: a fresh id for a rung that has none yet; every rung the editor creates already has one.
			id: rl.id || uuidv4(),
			// DEFAULT: "" — no location id yet; `buildLocation` always passes one, so only a direct caller hits this.
			location_id: locationId ?? "",
			roles: rl.roles,
			jobId: rl.jobId,
			censusStart: rl.censusStart,
			censusEnd: rl.censusEnd,
			amount: rl.amount,
			days: rl.days,
		})),
		rooms: data.rooms.map((roomName) => ({
			id: uuidv4(),
			// DEFAULT: "" — same as the rung's `location_id` above.
			schedule_template_id: locationId ?? "",
			room_name: roomName,
		})),
		cantMove: data.cantMove,
	};
};

/**
 * Groups days by (amount, amountRequired) pair.
 * Returns an array of [days[], amount, amountRequired] tuples.
 */
export const groupDaysByAmount = (
	days: string[],
	dayAmounts: Record<string, number>,
	dayAmountsRequired: Record<string, number>,
): Array<{ days: string[]; amount: number; amountRequired: number }> => {
	const groups = new Map<string, string[]>();
	for (const day of days) {
		// DEFAULT: 1 / 1 — unreachable from both callers, which pass an amount for every day they list
		// (`getFormConfigData` reads one per running day, `computeDayAmountsFromRatioLadder` lists only days a rung sets);
		// a day without one groups with the new-location staffing.
		const amt = dayAmounts[day] ?? 1;
		const req = dayAmountsRequired[day] ?? 1;
		const key = `${amt}-${req}`;
		const existing = groups.get(key);
		if (existing) {
			existing.push(day);
		} else {
			groups.set(key, [day]);
		}
	}
	return Array.from(groups.entries()).map(([key, groupDays]) => {
		const [amountStr, reqStr] = key.split("-");
		return {
			days: groupDays,
			amount: Number.parseInt(amountStr, 10),
			amountRequired: Number.parseInt(reqStr, 10),
		};
	});
};

/** For ratio departments: compute target (max) and required (min) from ratio ladder entries per day */
export const computeDayAmountsFromRatioLadder = (ratioLadder: RatioLadder[]): DayAmountFlatMaps => {
	const dayAmounts: Record<string, number> = {};
	const dayAmountsRequired: Record<string, number> = {};

	for (const entry of ratioLadder) {
		// DEFAULT: a rung saved without `days` applies to every weekday — the same reading as `rowDays`.
		const entryDays = entry.days ?? [...WEEKDAYS];
		for (const day of entryDays) {
			if (day in dayAmounts) {
				dayAmounts[day] = Math.max(dayAmounts[day], entry.amount);
				dayAmountsRequired[day] = Math.min(dayAmountsRequired[day], entry.amount);
			} else {
				dayAmounts[day] = entry.amount;
				dayAmountsRequired[day] = entry.amount;
			}
		}
	}

	const days = WEEKDAYS.filter((d) => d in dayAmounts);
	return { dayAmounts, dayAmountsRequired, days };
};

/**
 * In ratio departments the ladder is the source of truth for per-day amounts, so recompute them
 * from it before building a payload. Shared by the save and create handlers.
 */
export const resolveDayAmounts = (data: LocationConfigSlimData, canEditRatioLadder: boolean): LocationConfigSlimData => {
	// An empty ladder never reaches here in a ratio department: save is blocked until it covers 0–∞.
	if (!canEditRatioLadder) return data;
	const computed = computeDayAmountsFromRatioLadder(data.ratioLadder);
	const computedRows = groupDaysByAmount(computed.days, computed.dayAmounts, computed.dayAmountsRequired).map((group) => ({
		id: uuidv4(),
		...group,
	}));
	return { ...data, ...computed, dayAmountRows: computedRows };
};

/**
 * `base` sits between the defaults and the form data: the edit path passes the existing config so
 * server-owned fields it does not render survive the round trip, the create path passes `{}`.
 */
export const buildLocation = (
	data: LocationConfigSlimData,
	id: string,
	shiftTypes: ShiftType[],
	departmentId: string,
	base: Partial<DepartmentLocation>,
): DepartmentLocation =>
	({
		...DEFAULT_DEPARTMENT_LOCATION,
		...base,
		...slimDataToDepartmentLocation(data, shiftTypes, id),
		id,
		department_id: departmentId,
	}) satisfies DepartmentLocation;

/** What every "form → saved row" build reads from the department. */
export type FormRowContext = { canEditRatioLadder: boolean; shiftTypes: ShiftType[]; departmentId: string };

/**
 * The census area a form row saves: the one the EMR lists offer (`useCensusAreaOptions().offer`), so a unit, floor or
 * room the lists no longer have is dropped, as in the v1 editor; or `"asIs"`, the form's own unit, floor and rooms
 * untouched — for a shift added to an existing location, which takes that location's saved area rather than one the
 * admin picked from the lists.
 */
export type FormRowCensus = EmrMapping | "asIs";

/**
 * The one "form → saved row" recipe, shared by every editor that saves a shift config (the v1 editor's save and
 * create, the Locations page's expanded editor, its New location popup and its shift drafts). In a ratio department
 * the ladder sets the per-day amounts (`resolveDayAmounts`); the census area is `census`; `base` sits under the form
 * data so the saved fields the form doesn't render survive (see `buildLocation`). Pure.
 */
export const buildFormRow = (
	data: LocationConfigSlimData,
	census: FormRowCensus,
	id: string,
	base: Partial<DepartmentLocation>,
	ctx: FormRowContext,
): DepartmentLocation => {
	const resolved = resolveDayAmounts(data, ctx.canEditRatioLadder);
	const withCensus = census === "asIs" ? resolved : { ...resolved, ...census };
	return buildLocation(withCensus, id, ctx.shiftTypes, ctx.departmentId, base);
};

/**
 * The popup's shift hours, read in the department's timezone. An end at or before the start is the
 * next day's (an overnight shift). Either side is invalid when its "HH:mm" text doesn't parse.
 */
export const shiftTimesOf = (data: Pick<LocationConfigSlimData, "shiftStartTime" | "shiftEndTime">, timezone: string): { start: DateTime; end: DateTime } => {
	// Read IN the department's zone: a stored "06:00" is 6:00 AM there. In the app, `HospitalDataProvider` already
	// sets Luxon's default zone to the department's, so the times it reads are the same. Passing the zone only stops
	// relying on that having happened — in a spec, or on a first render before the provider has set it.
	const start = DateTime.fromFormat(data.shiftStartTime, "HH:mm", { zone: timezone });
	const end = DateTime.fromFormat(data.shiftEndTime, "HH:mm", { zone: timezone });
	return {
		start: start,
		end: end <= start ? end.plus({ day: 1 }) : end,
	};
};
